import { Bid, BidVendorRow, BidVendorScenarioBidRow, BidVendorScenarioRow } from '../../_models/bid';
import { BidFieldPlan, FieldPlanProject, FieldPlanVendor } from '../../_models/field-plan';
import { SamplePlanCountry, SamplePlanSpec } from './bid-spec-parsing';
import { specKeys, splitInProportion } from './bid-tracker';

/** An import picked in the clone prompt, carried to the draft in the clone state. */
export interface FieldPlanImport {
  how: 'started' | 'ended';
  plan: BidFieldPlan;
}

type ImportHow = FieldPlanImport['how'];

/**
 * The clone's vendors and field plan from how a NAV project ran (AC-10.9), per Lisa's choice of figures:
 * started = the project's set-up when its first complete came in (each vendor's allocation and CPI, and
 * the segment's IR, LOI and client CPI); ended = each vendor's final CPI (closure's blended CPI once
 * closure is confirmed) and the completes it delivered, with the project's actual IR, LOI and client CPI.
 * Vendors with nothing to carry are left out.
 *
 * Both carry the vendors' figures as they are, and the spec's sample size becomes their total (Lisa,
 * 2 Nov 2026); a project serving several specs splits each vendor's figure across them by their sample
 * sizes. When the set-up at the first complete can't be rebuilt (no complete yet, or several segments),
 * started falls back to each vendor's share of the booked mix (else as set now) sized to the clone's own
 * sample size, and its first CPI in field. Each spec reads the project for its country (a project with
 * no country is US); a family of one project serves every spec. A spec whose country has no project
 * keeps no vendor bids.
 *
 * `keepSampleSize` is for a renewal, which is bid for the year from last year's full year (REN-03):
 * its sample sizes stay, and the wave's vendor figures only give the mix, sized to them. Rates are
 * imported as usual.
 */
export function importFieldPlan(bid: Partial<Bid>, imported: FieldPlanImport, keepSampleSize = false): Pick<Bid, 'vendors' | 'vendorScenarios' | 'samplePlan'> {
  const { how, plan } = imported;
  const samplePlan: SamplePlanCountry[] = bid.samplePlan ?? [];
  const keys = specKeys(samplePlan);
  const booked = new Map(plan.bookedSpecs.map(s => [s.key, s.vendors]));
  const scenarios = new Map<string, BidVendorScenarioRow>(
    (bid.vendorScenarios ?? []).map(s => [s.key, { ...s, bids: [] }]));
  const used = new Map<string, Set<string>>(); // vendor -> countries it's used in
  const served = new Map<FieldPlanProject, SamplePlanSpec[]>();
  for (const country of samplePlan) {
    const project = projectFor(plan, country.country);
    if (project) served.set(project, [...(served.get(project) ?? []), ...country.audiences.flatMap(a => a.specs)]);
  }
  const specUpdates = new Map<SamplePlanSpec, Partial<SamplePlanSpec>>();

  for (const country of samplePlan) {
    const project = projectFor(plan, country.country);
    if (!project) continue;
    const asRun = how === 'ended' || rebuiltStart(project);
    const vendors = project.vendors.filter(v => total(v, how, asRun) > 0);
    if (!vendors.length) continue;
    const field = fieldFigures(plan, project, how, asRun);

    for (const spec of country.audiences.flatMap(a => a.specs)) {
      const key = keys.get(spec);
      if (!key) continue;
      const size = spec.sampleSize ?? 0;
      let allocations: number[];
      const keepSize = keepSampleSize && size > 0;
      if (asRun && keepSize) {
        allocations = splitInProportion(vendors.map(v => total(v, how, asRun)), size);
      } else if (asRun) {
        allocations = vendors.map(v => runShare(total(v, how, asRun), served.get(project) ?? [spec], spec));
      } else {
        // The booked mix for this very spec when the won bid had it, else the project's.
        const bookedHere = booked.get(key);
        const bookedWeights = bookedHere && vendors.map(v => bookedHere.find(b => sameId(b.vendorId, v.vendorId))?.allocN ?? 0);
        const weights = bookedWeights?.some(w => w > 0) ? bookedWeights : vendors.map(v => total(v, how, asRun));
        allocations = size > 0 ? splitInProportion(weights, size) : weights;
      }
      specUpdates.set(spec, {
        ...(asRun && !keepSize ? { sampleSize: allocations.reduce((s, n) => s + n, 0) } : {}),
        // The API's IR is a percentage; a spec holds it as a fraction (5% is 0.05).
        ...(field.ir != null ? { incidenceRate: Math.round(field.ir) / 100 } : {}),
        ...(field.loi != null ? { loi: Math.round(field.loi) } : {}),
      });

      const bids: BidVendorScenarioBidRow[] = vendors
        .map((v, i) => ({ v, alloc: allocations[i] }))
        .filter(({ alloc }) => alloc > 0)
        .map(({ v, alloc }) => {
          const cpi = how === 'ended' ? v.finalCpi : asRun ? (v.startedCpi ?? v.firstCpi) : v.firstCpi;
          if (!used.has(v.vendorId)) used.set(v.vendorId, new Set());
          used.get(v.vendorId)!.add(country.country);
          return {
            vendorId: v.vendorId,
            cpi: cpi != null ? String(roundCents(cpi)) : undefined,
            cpiSource: 'manual',
            selected: true,
            allocN: alloc,
          };
        });
      const scenario = scenarios.get(key) ?? { key };
      scenarios.set(key, { ...scenario, bids, ...(field.sellCpi != null ? { sellCpi: roundCents(field.sellCpi) } : {}) });
    }
  }

  // Global bids assign each vendor the countries it covers; other structures leave that empty.
  const global = samplePlan.length > 1;
  const vendors: BidVendorRow[] = [...used.entries()].map(([vendorId, countries]) => ({
    vendorId,
    sent: false,
    sentAt: null,
    countries: global ? [...countries] : undefined,
  }));
  const updated = specUpdates.size === 0 ? samplePlan : samplePlan.map(c => ({
    ...c,
    audiences: c.audiences.map(a => ({
      ...a,
      specs: a.specs.map(s => specUpdates.has(s) ? { ...s, ...specUpdates.get(s) } : s),
    })),
  }));
  return { vendors, vendorScenarios: [...scenarios.values()], samplePlan: updated };
}

/** True when the API could rebuild the project's set-up at its first complete. */
export function rebuiltStart(project: FieldPlanProject): boolean {
  return project.vendors.some(v => v.startedAllocation != null);
}

/** IR, LOI and client CPI the import puts on the spec; nothing when started couldn't be rebuilt. Closure
 *  prices the client for the whole family, so its CPI is used only when one project serves the bid;
 *  otherwise each project's own completes say what the client paid. */
function fieldFigures(plan: BidFieldPlan, project: FieldPlanProject, how: ImportHow, asRun: boolean):
  { ir: number | null; loi: number | null; sellCpi: number | null } {
  if (how === 'ended') {
    const closure = plan.projects.length === 1 ? plan.closureSellCpi : null;
    return { ir: project.actualIr, loi: project.actualLoi, sellCpi: closure ?? project.sellCpi };
  }
  return asRun
    ? { ir: project.startedIr, loi: project.startedLoi, sellCpi: project.startedSellCpi }
    : { ir: null, loi: null, sellCpi: null };
}

/** One spec's part of a vendor's figure, when its project served several specs: split by their
 *  sample sizes, or evenly when none has one. */
function runShare(value: number, specs: SamplePlanSpec[], spec: SamplePlanSpec): number {
  if (specs.length <= 1) return value;
  const sizes = specs.map(s => s.sampleSize ?? 0);
  const weights = sizes.some(n => n > 0) ? sizes : specs.map(() => 1);
  return splitInProportion(weights, value)[specs.indexOf(spec)];
}

/** "Field plan imported from NAV 30100 (how it ended)." with a warning while the wave is still in field,
 *  and, on a renewal, that the sample sizes are last year's. */
export function fieldPlanImportNote(imported: FieldPlanImport, keepSampleSize = false): string {
  const { how, plan } = imported;
  const sized = keepSampleSize ? ' Allocations are sized to last year\'s sample sizes.' : '';
  const inField = plan.vendorClosedAt ? '' : ' It hasn\'t been closed, so the figures may still change.';
  return `Field plan imported from ${plan.projectCode} (how it ${how}).${sized}${inField}`;
}

function projectFor(plan: BidFieldPlan, country: string): FieldPlanProject | null {
  const match = plan.projects.find(p => p.countryCodes.some(c => c.toUpperCase() === country?.toUpperCase()));
  return match ?? (plan.projects.length === 1 ? plan.projects[0] : null);
}

function total(v: FieldPlanVendor, how: ImportHow, asRun: boolean): number {
  if (how === 'ended') return v.completes;
  return asRun ? (v.startedAllocation ?? 0) : (v.allocationBooked ?? v.allocationNow);
}

function roundCents(value: number): number {
  return Math.round(value * 100) / 100;
}

function sameId(a: string, b: string): boolean {
  return a?.toLowerCase() === b?.toLowerCase();
}
